import { UNGROUPED_RESOURCES_GROUP_ID } from '@/console/features/groups/components/group-list/group-list.constants';
import { AssetTypeEnum } from '@/shared/types/types.generated';
import { capitalizeString } from '@/shared/utils/format';

import { api } from '../../_generated/api/assets-api.generated';

export const documentsApi = api.enhanceEndpoints({
  addTagTypes: [
    'Document' as const,
    'DocumentVersions' as const,
    'Groups' as const,
    ...Object.values(AssetTypeEnum).flatMap((type) => capitalizeString(type)),
  ],
  endpoints: {
    ReadAllDocuments: {
      providesTags: (_, __, args) => {
        const tags = [{ type: 'Document' as const, id: 'LIST' }];
        const filteredGroup = args?.filter?.groupId;
        const groupId = filteredGroup?.contains ?? UNGROUPED_RESOURCES_GROUP_ID;
        if (filteredGroup) {
          return [...tags, { type: 'Groups' as const, id: groupId }];
        }
        return tags;
      },
    },
    CreateDocument: {
      invalidatesTags: [
        { type: 'Document' as const, id: 'LIST' },
        'Groups' as const,
      ],
    },
    ReadDocument: {
      providesTags: (_, __, args) => [
        { type: 'Document' as const, id: args.documentId },
      ],
    },
    ReadLinkedDocumentsForAsset: {
      providesTags: (_, __, args) => [
        { type: capitalizeString(args.assetType), id: args.assetId },
      ],
    },
    ReadLinkedAssetsFromDocument: {
      providesTags: (_, __, args) => [
        { type: 'Document' as const, id: args.documentId },
      ],
    },
    DeleteDocument: {
      invalidatesTags: [{ type: 'Document', id: 'LIST' }, 'Groups' as const],
    },
    ReadVersionsForDocument: {
      providesTags: (_, __, args) => [
        { type: 'DocumentVersions' as const, id: args.documentId },
      ],
    },
    UpdateDocument: {
      invalidatesTags: (_, __, args) => [
        { type: 'Document' as const, id: args.documentId },
        { type: 'Document' as const, id: 'LIST' },
        'Groups' as const,
      ],
    },
    UpdateDocumentVersion: {
      invalidatesTags: (_, __, args) => [
        { type: 'DocumentVersions' as const, id: args.documentId },
      ],
    },
    CompleteDocumentMultipartUpload: {
      invalidatesTags: (_, __, args) => [
        { type: 'Document' as const, id: args.documentId },
        { type: 'Document' as const, id: 'LIST' },
        { type: 'DocumentVersions' as const, id: args.documentId },
        'Groups' as const,
      ],
    },
  },
});

export const {
  useCreateDocumentMutation,
  useCreateDocumentVersionMutation,
  useCompleteDocumentMultipartUploadMutation,
  useLazyDownloadDocumentVersionQuery,
  useReadDocumentQuery,
  useReadAllDocumentsQuery,
  useReadLinkedDocumentsForAssetQuery,
  useReadLinkedAssetsFromDocumentQuery,
  useDeleteDocumentMutation,
  useReadVersionsForDocumentQuery,
  useUpdateDocumentMutation,
  useUpdateDocumentVersionMutation,
  useReadDocumentNamesForRoleRowQuery,
  useReadDocumentsByIdsQuery,
} = documentsApi;
